"use client";

import React from "react";
import { Phone, Mail, MapPin, CircleCheck, Loader, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="39026" className="flex flex-col items-center justify-center text-center h-full min-h-[400px] p-8 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-6">
          <CircleCheck className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-serif font-medium text-foreground mb-2">Nachricht gesendet!</h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          {successMessage || "Vielen Dank für deine Nachricht. Ich werde mich in einem Moment der Ruhe bei dir melden."}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300">
          Neue Nachricht senden
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Bitte gib deinen Namen ein."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/70 font-sans text-xs uppercase tracking-wider">Name <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input placeholder="Dein vollständiger Name" {...field} className="border-b border-t-0 border-l-0 border-r-0 rounded-none border-primary/20 focus-visible:ring-0 focus-visible:border-primary bg-transparent transition-all duration-300" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-Mail ist erforderlich.",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Bitte gib eine gültige E-Mail-Adresse ein."
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/70 font-sans text-xs uppercase tracking-wider">E-Mail-Adresse <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="beispiel@email.ch" {...field} className="border-b border-t-0 border-l-0 border-r-0 rounded-none border-primary/20 focus-visible:ring-0 focus-visible:border-primary bg-transparent transition-all duration-300" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" rules={{
        required: "Telefonnummer ist erforderlich."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/70 font-sans text-xs uppercase tracking-wider">Telefonnummer <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+41 76 123 45 67" {...field} className="border-b border-t-0 border-l-0 border-r-0 rounded-none border-primary/20 focus-visible:ring-0 focus-visible:border-primary bg-transparent transition-all duration-300" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="project_type" rules={{
        required: "Bitte wähle ein Anliegen."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/70 font-sans text-xs uppercase tracking-wider">Anliegen <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <div className="relative">
                  <select className="flex h-9 w-full border-b border-t-0 border-l-0 border-r-0 rounded-none border-primary/20 bg-transparent px-3 py-1 text-sm transition-colors focus-visible:outline-none focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50" {...field} value={field.value || ""}>
                    <option value="" disabled>Bitte auswählen</option>
                    <option value="Begleitung">Persönliche Begleitung</option>
                    <option value="Retreat">Retreat-Teilnahme</option>
                    <option value="Workshop">Workshop & Seminare</option>
                    <option value="Sonstiges">Allgemeine Anfrage</option>
                  </select>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="budget" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/70 font-sans text-xs uppercase tracking-wider">Zeitraum (optional)</FormLabel>
            <FormControl>
              <Input placeholder="z. B. Frühjahr 2024" {...field} className="border-b border-t-0 border-l-0 border-r-0 rounded-none border-primary/20 focus-visible:ring-0 focus-visible:border-primary bg-transparent transition-all duration-300" />
            </FormControl>
            <FormDescription className="text-[10px] italic">
              Nenne mir deinen bevorzugten Zeitrahmen.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="description" rules={{
      required: "Bitte beschreibe dein Anliegen."
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/70 font-sans text-xs uppercase tracking-wider">Ihre Nachricht <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <Textarea placeholder="Teile mir mit, wie ich dich auf deinem Weg begleiten kann" className="min-h-[120px] border-b border-t-0 border-l-0 border-r-0 rounded-none border-primary/20 focus-visible:ring-0 focus-visible:border-primary bg-transparent resize-none transition-all duration-300" {...field} />
            </FormControl>
            <FormDescription className="text-[10px] italic">
              Ein paar Worte zu deiner aktuellen Situation helfen uns bei der Vorbereitung.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-sm text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 text-sm font-sans uppercase tracking-widest shadow-sm transition-all duration-500">
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Wird gesendet...
          </> : <>
            Nachricht senden.
            <ArrowRight className="ml-2 h-4 w-4" />
          </>}
      </Button>
      
      <p className="text-[10px] text-center text-muted-foreground mt-4 uppercase tracking-tighter">
        Durch das Absenden akzeptierst du unsere <Link to="#" className="underline hover:text-primary transition-colors">Datenschutzbestimmungen</Link>.
      </p>
    </Form>;
}
export default function ProjectInquiry() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section ref={ref} className={`py-24 px-6 bg-background transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground mb-6">Lass uns gemeinsam <br /><span className="italic text-primary">Wandeln</span></h2>
            <p className="text-lg text-muted-foreground mb-12 max-w-md font-sans">
              Hast du Fragen zu meiner Arbeit oder möchtest du einen Termin für eine Begleitung vereinbaren? Ich freue mich auf deine Nachricht.
            </p>
            
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-primary/5 flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-sans font-medium text-foreground text-sm uppercase tracking-wider">E-Mail</h4>
                  <p className="text-muted-foreground">renata.wandelherz@gmail.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-primary/5 flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-sans font-medium text-foreground text-sm uppercase tracking-wider">Telefon</h4>
                  <p className="text-muted-foreground">+41 76 371 73 78</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-primary/5 flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-sans font-medium text-foreground text-sm uppercase tracking-wider">Ort</h4>
                  <p className="text-muted-foreground">Zürich, Frankreich</p>
                </div>
              </div>
            </div>
          </div>

          <Card className="border-none shadow-sm bg-card/50 backdrop-blur-sm">
            <CardContent className="p-6 md:p-8">
              <FormProvider formId="project-inquiry" sectionName="Project Inquiry" defaultValues={{
              name: "",
              email: "",
              phone: "",
              project_type: "",
              budget: "",
              description: ""
            }}>
                <InquiryForm />
              </FormProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}